// /demo: the real dashboard, filled with one fictional dealership's day.
// Public (no sign-in), so it never touches real call data. See src/lib/demo-data.ts.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { getDemoDay, type DemoDay } from "@/lib/demo-data";
import { DashboardView } from "@/components/dashboard-view";
import { Button } from "@/components/ui/button";
import eeswebLogo from "@/assets/eesweb-logo.png";
import { Info } from "lucide-react";

export const Route = createFileRoute("/demo")({
  head: () => ({ meta: [{ title: "Demo dashboard — EESWEB" }] }),
  component: DemoPage,
});

function DemoPage() {
  // Generated in the browser so "now" is the visitor's clock (the server's would be UTC).
  const [day, setDay] = useState<DemoDay | null>(null);
  useEffect(() => {
    setDay(getDemoDay());
    // Roll the clock forward so the day keeps moving while the page is open.
    const t = setInterval(() => setDay(getDemoDay()), 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-3 px-4 md:px-8">
          <img src={eeswebLogo} alt="EESWEB" className="h-7 w-auto dark:brightness-0 dark:invert dark:opacity-90" />
          <Button asChild size="sm"><Link to="/auth">Sign in</Link></Button>
        </div>
      </header>
      <main className="p-4 md:p-8">
        <DashboardView
          title="Demo Motors"
          subtitle={day ? `${day.dayLabel}${day.isToday ? "" : " (yesterday)"} · sample dealership` : "Sample dealership"}
          refreshNote="Sample data · updates every minute"
          todayCalls={day?.calls}
          liveCalls={day ? day.live : []}
          ivr={day?.ivr}
          linkCalls={false}
        >
          <div className="flex items-start gap-3 rounded-2xl border border-info/30 bg-info/5 px-4 py-3 text-sm">
            <Info className="mt-0.5 size-4 shrink-0 text-info" />
            <p className="text-foreground">
              This is what a dealership sees, filled with a made-up day of calls. Phone numbers are masked.
              {day && !day.isToday && " It's early, so you're seeing yesterday's full day."}{" "}
              <Link to="/auth" className="font-medium text-primary hover:underline">Sign in</Link> to see your own calls.
            </p>
          </div>
        </DashboardView>
      </main>
    </div>
  );
}
